import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { EAlertState } from "../../../../shared/oversight-core/enums/alert-state";
import { IAlertOptions } from "../../../../shared/oversight-core/interfaces/alert-options";
import FilteringSidePanel from "../../../../shared/oversight-core/shared-components/filtering-side-panel/filtering-side-panel";
import AppDatePicker from "../../../../shared/oversight-core/ui-elements/app-date-picker/app-date-picker";
import AppSelect, {
  Option,
} from "../../../../shared/oversight-core/ui-elements/app-select/app-select";
import CheckBox from "../../../../shared/oversight-core/ui-elements/check-box/check-box";
import {
  dayFromEndExclusiveIso,
  dayFromStartIso,
  defaultFromDay,
  fromDayStartIso,
  getDayRangeError,
  toDayEndExclusiveIso,
} from "../../components/alert-filter-fields/alert-filter-dates";
import AlertFilterFields, {
  countAlertFilters,
  IAlertFilterValues,
} from "../../components/alert-filter-fields/alert-filter-fields";
import type { IErrorLogFilters } from "../sites-error-log";

interface IProps {
  show: boolean;
  onClose: () => void;
  updateFilters: (changes: Partial<IErrorLogFilters>) => void;
  setFilterBadgeValue: React.Dispatch<React.SetStateAction<number>>;
  filters: IErrorLogFilters;
  solarBrandList: Option[];
  alertOptions?: IAlertOptions;
}

const solarBrandDefaultOption: Option = {
  value: "",
  label: "Select a solar brand",
};

const defaultAlertValues: IAlertFilterValues = {
  severity: [],
  alertState: EAlertState.OPEN,
  alertName: [],
};

const SitesErrorLogFilterPanel = (props: IProps) => {
  const {
    show,
    filters,
    onClose,
    updateFilters,
    setFilterBadgeValue,
    solarBrandList,
    alertOptions,
  } = props;

  const [selectedSolarBrand, setSelectedSolarBrand] = useState<Option>({
    ...solarBrandDefaultOption,
  });
  const [alertValues, setAlertValues] = useState<IAlertFilterValues>({
    ...defaultAlertValues,
  });
  const [filterByDate, setFilterByDate] = useState(false);
  // CheckBox is uncontrolled; a new key remounts it unchecked after Clear All.
  const [dateCheckBoxKey, setDateCheckBoxKey] = useState(0);
  const [fromDate, setFromDate] = useState(defaultFromDay);
  const [toDate, setToDate] = useState(new Date());

  const dateRangeError = filterByDate
    ? getDayRangeError(fromDate, toDate)
    : undefined;
  const dateRangeValid = !dateRangeError;

  // The form shows what is applied each time the drawer opens, so a choice that was never applied
  // does not come back after the drawer is closed.
  useEffect(() => {
    if (!show) {
      return;
    }

    const hasDates = !!(filters.from && filters.to);
    setSelectedSolarBrand(
      (filters.generationServiceProviderId &&
        solarBrandList.find(
          (brand) => brand.value === filters.generationServiceProviderId,
        )) || { ...solarBrandDefaultOption },
    );
    setAlertValues({
      severity: filters.severity,
      alertState: filters.alertState,
      alertName: filters.alertName,
    });
    setFilterByDate(hasDates);
    setDateCheckBoxKey((key) => key + 1);
    setFromDate(hasDates ? dayFromStartIso(filters.from) : defaultFromDay());
    setToDate(hasDates ? dayFromEndExclusiveIso(filters.to) : new Date());
  }, [show, filters, solarBrandList]);

  // The badge reflects what is applied, not what is being edited in the panel.
  useEffect(() => {
    setFilterBadgeValue(
      (filters.generationServiceProviderId ? 1 : 0) +
        (filters.from || filters.to ? 1 : 0) +
        countAlertFilters(
          {
            severity: filters.severity,
            alertState: filters.alertState,
            alertName: filters.alertName,
          },
          EAlertState.OPEN,
        ),
    );
  }, [filters, setFilterBadgeValue]);

  return (
    <FilteringSidePanel
      isOpen={show}
      onClose={onClose}
      isFilterDisabled={!dateRangeValid}
      onFilter={() => {
        if (!dateRangeValid) {
          return;
        }

        updateFilters({
          generationServiceProviderId: selectedSolarBrand.value,
          ...alertValues,
          from: filterByDate ? fromDayStartIso(fromDate) : "",
          to: filterByDate ? toDayEndExclusiveIso(toDate) : "",
        });
        onClose();
      }}
      onClearAll={() => {
        // Clears the form and the applied filters back to the defaults, and closes the drawer.
        setSelectedSolarBrand({ ...solarBrandDefaultOption });
        setAlertValues({ ...defaultAlertValues });
        setFilterByDate(false);
        setDateCheckBoxKey((key) => key + 1);
        setFromDate(defaultFromDay());
        setToDate(new Date());
        updateFilters({
          generationServiceProviderId: "",
          ...defaultAlertValues,
          from: "",
          to: "",
        });
        onClose();
      }}
    >
      <>
        <AppSelect
          className="text-light font-size-14"
          selectedValue={selectedSolarBrand}
          label="Solar Brand"
          options={solarBrandList}
          onChangeOption={(selectedOption) => {
            setSelectedSolarBrand(selectedOption);
          }}
          isRequired={false}
          labelFontSize="font-size-14"
        />
        <div className="mt-4">
          <AlertFilterFields
            values={alertValues}
            onChange={setAlertValues}
            options={alertOptions}
          />
        </div>
        <Row className="mt-4">
          <Col>
            <CheckBox
              key={dateCheckBoxKey}
              label="Filter by start date"
              checked={filterByDate}
              onChange={setFilterByDate}
              labelFontSize="font-size-14"
            />
          </Col>
        </Row>
        {filterByDate && (
          <>
            <Row className="mt-3">
              <Col>
                <AppDatePicker
                  label="From"
                  selectedDate={fromDate}
                  onChange={setFromDate}
                  isInput
                  inputDateFormat="dd / MM / yyyy"
                  fontSize={12}
                />
              </Col>
            </Row>
            <Row className="mt-4">
              <Col>
                <AppDatePicker
                  label="To"
                  selectedDate={toDate}
                  onChange={setToDate}
                  isInput
                  inputDateFormat="dd / MM / yyyy"
                  fontSize={12}
                  error={dateRangeError}
                />
              </Col>
            </Row>
          </>
        )}
      </>
    </FilteringSidePanel>
  );
};

export default SitesErrorLogFilterPanel;
